/**
 * Utility functions for date formatting and relative time
 */

/**
 * Format date for display with relative time
 * @param {string} dateString - ISO date string
 * @param {Function} t - Translation function (optional)
 * @returns {Object} - Object with date, time, and relative properties
 */
export const formatImageDate = (dateString, t = null) => {
  if (!dateString) return { date: 'Unknown date', time: '', relative: 'Unknown' };

  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    relative: getRelativeTime(date, t),
  };
};

/**
 * Get relative time (e.g., "2 days ago")
 * @param {Date} date - Date object
 * @param {Function} t - Translation function (optional)
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (date, t = null) => {
  const now = new Date();

  // Reset both dates to start of day to compare only the date part
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Calculate the difference in days
  const diffInMs = startOfToday.getTime() - startOfDate.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return t ? t('skinMoles.relativeTime.today') : 'Today';
  if (diffInDays === 1) return t ? t('skinMoles.relativeTime.yesterday') : 'Yesterday';
  if (diffInDays <= 7)
    return t
      ? t('skinMoles.relativeTime.daysAgo').replace('{{count}}', diffInDays)
      : `${diffInDays} days ago`;
  if (diffInDays <= 30)
    return t
      ? t('skinMoles.relativeTime.weeksAgo').replace('{{count}}', Math.floor(diffInDays / 7))
      : `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays <= 365)
    return t
      ? t('skinMoles.relativeTime.monthsAgo').replace('{{count}}', Math.floor(diffInDays / 30))
      : `${Math.floor(diffInDays / 30)} months ago`;
  return t
    ? t('skinMoles.relativeTime.yearsAgo').replace('{{count}}', Math.floor(diffInDays / 365))
    : `${Math.floor(diffInDays / 365)} years ago`;
};

/**
 * Simple date formatting for mole creation dates
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date and time
 */
export const formatMoleDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  const date = new Date(dateString);
  return (
    date.toLocaleDateString() +
    ' ' +
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  );
};
